<template>
  <v-flex d-flex>
    <v-container fluid>
      <v-fab-transition>
        <v-btn v-show="toolbarReady" fixed bottom right dark fab color="pink" @click="refresh" :loading="loading">
          <v-icon>cached</v-icon>
        </v-btn>
      </v-fab-transition>
      <v-card>
        <v-tabs v-model="tabActive">
          <v-tab v-for="item in tableDisplayItems" :key="item.key" ripple>
            <v-fade-transition>
              <v-icon
                v-html="'mdi-checkbox-blank-circle'"
                :color="states[item.key].status"
                class="mr-2"
                style="font-size: 1em"
              />
            </v-fade-transition>
            <span>{{item.key}}</span>
          </v-tab>
          <v-tab-item v-for="item in tableDisplayItems" :key="item.key">
            <MonitorTable
              ref="monitorTables"
              :key="item.key"
              :title="item.key"
              :items="item.value"
              :ready="tables.ready"
              :loading="states[item.key].loading"
              class=""
              @statusChanged="status => states[item.key].status = status"
            />
          </v-tab-item>
        </v-tabs>
      </v-card>
    </v-container>
  </v-flex>
</template>

<script>
import MongoService from '../../services/mongo.service'
import MonitorTable from './MonitorTable'
import { groupArrayByObjectKey, objectToArray, getProp } from '../../libs/utils'

const RIPPLE_TYPE = 'ripple'

export default {
  data () {
    return {
      states: {
        'bot': {
          loading: false,
          status: null,
          minValueCondition: -Infinity,
          checkIncorrect (account, client) {
            return account.totals && account.totals.balance_USD < this.minValueCondition
          }
        },
        'client_buffer': {
          loading: false,
          status: null,
          minValueCondition: 3000,
          checkIncorrect (account, client) {
            return account.totals && account.totals.balance_USD < this.minValueCondition
          }
        },
        [RIPPLE_TYPE]: {
          loading: false,
          status: null,
          minValueCondition: 1000000,
          checkIncorrect (account, client) {
            const sumBalance = client.value.reduce((sum, _account) => {
              return sum + ((_account.totals && _account.totals.balance_USD) || 0)
            }, 0)
            return sumBalance < this.minValueCondition
          }
        }
      },
      tabActive: null,
      tables: {
        ready: false,
        data: [],
        cache: {
          live: [],
          rippleTimeout: null,
          [RIPPLE_TYPE]: []
        }
      }
    }
  },
  computed: {
    toolbarReady () {
      return this.tables.ready
    },
    loading () {
      return this.states['bot'].loading || this.states['client_buffer'].loading || this.states[RIPPLE_TYPE].loading
    },
    tableDisplayItems () {
      let items = {
        'bot': [],
        'client_buffer': [],
        [RIPPLE_TYPE]: []
      }
      if (this.tables.data.length) {
        items = groupArrayByObjectKey(this.tables.data, 'type')
        Object.keys(items).forEach(key => {
          items[key] = objectToArray(groupArrayByObjectKey(items[key], 'client_id'))
            .map(client => {
              client.minValueCondition = this.states[key].minValueCondition
              client.prop = 'client_id'
              client.errors = client.value.map(account => account.error).filter(x => x)
              client.incorrects = client.value
                .filter(
                  account => {
                    account.incorrect = this.states[key].checkIncorrect(account, client)
                    account.success = !account.error && !account.incorrect
                    return account.incorrect
                  }
                )
              client.success = !client.errors.length && !client.incorrects.length
              client.summary = {
                'available_USD': client.value.reduce((c, account) => {
                  return c + (account.totals && account.totals['available_USD'] ? +account.totals['available_USD'] : 0)
                }, 0),
                'balance_USD': client.value.reduce((c, account) => {
                  return c + (account.totals && account.totals['balance_USD'] ? +account.totals['balance_USD'] : 0)
                }, 0)
              }
              return client
            })
        })
      }
      console.log(objectToArray(items))
      return objectToArray(items)
    }
  },
  async mounted () {
    await this.refresh()
  },
  methods: {
    setLoader (val) {
      this.states['bot'].loading = val
      this.states['client_buffer'].loading = val
      this.states[RIPPLE_TYPE].loading = val
    },
    async refresh () {
      this.setLoader(true)

      await Promise.all([
        this.getLiveBalance().catch(e => {
          console.error(e)
          this.tabActive = 0
        }),
        this.getRippleBalance().catch(e => {
          console.error(e)
          this.tabActive = 0
        })
      ])

      this.setLoader(false)
      this.setTableData(this.tables.cache.live, this.tables.cache.ripple)

      this.tables.ready = true
    },
    setTableData (live, ripple) {
      this.tables.data = [...this.tables.cache.live, ...this.tables.cache.ripple]
    },
    async getLiveBalance () {
      const result = await MongoService.balances.live()
      this.tables.cache.live = result
      return this.tables.cache.live
    },
    async getRippleBalance (accountIds) {
      const result = await MongoService.balances.ripple(accountIds)
      result.forEach(balance => {
        const cachedBalance = this.tables.cache.ripple.filter(b => b.account_id === balance.account_id)[0]
        if (cachedBalance) {
          const cachedIndex = this.tables.cache.ripple.indexOf(cachedBalance)
          this.tables.cache.ripple[cachedIndex] = balance
        } else {
          this.tables.cache.ripple.push(balance)
        }
      })
      const timeoutBalanceIds = result
        .filter(balance => getProp(balance, 'error.name') === 'TIMEOUT')
        .map(balance => balance.account_id)
      if (timeoutBalanceIds.length) {
        this.tables.cache.rippleTimeout !== null && clearTimeout(this.tables.cache.rippleTimeout)
        setTimeout(() => {
          this.states[RIPPLE_TYPE].loading = true
        }, 1000)
        this.tables.cache.rippleTimeout = setTimeout(() => {
          this.setTableData(this.tables.cache.live, this.tables.cache.ripple)
          this.getRippleBalance(timeoutBalanceIds)
        }, 40000)
      } else {
        this.states[RIPPLE_TYPE].loading = false
        this.setTableData(this.tables.cache.live, this.tables.cache.ripple)
      }
      return this.tables.cache.ripple
    }
  },
  components: {
    MonitorTable
  }
}
</script>
